import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import { DreamTableFilterComponent, DreamTableMakeFilter } from "../dreamTableTypes";
import { describeEnumSelection } from "./filterValueStatus";
import Tooltip from "@mui/material/Tooltip";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import { styled } from "@mui/material/styles";

export const EnumMultiSelectCheckboxFilter = <T extends string>({
  id,
  label,
  value = [],
  setValue,
  enumToFriendlyMap,
  optionsToDisable,
  disabledTooltipTitle = "",
}: Parameters<DreamTableFilterComponent>[0] & {
  value: T[];
  setValue: (newValue: T[], changedValue?: T) => void;
  enumToFriendlyMap: Record<string, string>;
  optionsToDisable?: string[];
  disabledTooltipTitle?: React.ReactNode;
}) => {
  const handleSelection = (id: string) => (e: React.ChangeEvent | React.MouseEvent) => {
    e.preventDefault();
    const currentValues = value || [];
    let newValues: string[];
    // If already selected, remove it
    if (currentValues.includes(id)) {
      newValues = currentValues.filter((item: string) => item !== id);
    }
    // If not selected, add it
    else {
      newValues = [...currentValues, id];
    }
    // Nothing selected has to be handled as `[]` / no filter
    setValue(newValues.length ? newValues : [], id);
  };
  return (
    <CheckboxFormControl component="fieldset">
      <FormGroup>
        <FormLabel component="legend">{label}</FormLabel>
        {Object.entries(enumToFriendlyMap).map(([key, friendlyKey]) => {
          const disabled = optionsToDisable?.includes(key);
          return (
            <Tooltip key={key} arrow title={disabled ? disabledTooltipTitle : ""}>
              <span onClick={handleSelection(key)}>
                <FormControlLabel
                  key={key}
                  label={friendlyKey}
                  disabled={disabled}
                  control={
                    <Checkbox
                      name={key}
                      checked={value?.includes(key)}
                      onChange={handleSelection(key)}
                      slotProps={{
                        input: {
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-expect-error
                          "data-testid": kebabCase(`${label ?? id}-option-${friendlyKey}`),
                        },
                      }}
                    />
                  }
                />
              </span>
            </Tooltip>
          );
        })}
      </FormGroup>
    </CheckboxFormControl>
  );
};

export const makeDreamTableEnumMultiSelectCheckboxFilter = (
  enumToFriendlyMap: Record<string, string>
): DreamTableMakeFilter => ({
  FilterComponent: (props) => EnumMultiSelectCheckboxFilter({ enumToFriendlyMap, ...props }),
  describeValue: (value) => describeEnumSelection(enumToFriendlyMap, value),
});

const CheckboxFormControl = styled(FormControl)`
  width: 100%;
` as typeof FormControl;
